import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Eye, ChevronRight, Lock } from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";

export default function Associations() {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/associations")
            .then((r) => setItems(r.data))
            .catch(() => setItems([]));
    }, []);

    return (
        <Layout
            title="Associations"
            subtitle="Connais le terrain. Inspire la confiance."
        >
            {items === null ? (
                <div className="glass-card rounded-3xl p-10 text-center text-white/50">
                    Chargement...
                </div>
            ) : items.length === 0 ? (
                <div
                    className="glass-card rounded-3xl p-8 sm:p-10 text-center"
                    data-testid="associations-empty"
                >
                    <div className="w-14 h-14 rounded-2xl bg-shariti-orange/15 flex items-center justify-center mx-auto mb-3">
                        <Lock className="w-6 h-6 text-shariti-orange" />
                    </div>
                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-2">
                        Aucune association attribuée
                    </p>
                    <h3 className="text-xl font-extrabold text-white mb-2 tracking-tight">
                        En attente d'attribution
                    </h3>
                    <p className="text-sm text-white/65 font-light max-w-md mx-auto leading-relaxed">
                        Cette section te donnera accès aux contenus des associations sur lesquelles tu travailles dès que ton manager t'aura ouvert les accès.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {items.map((a, i) => {
                        const totalSections = 5; // mission, actions, chiffres, argumentaire, faq
                        const progress = Math.min(
                            100,
                            Math.round(
                                ((a.sections_read_count || 0) / totalSections) * 100,
                            ),
                        );
                        return (
                            <motion.div
                                key={a.association_id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.04 }}
                            >
                                <Link
                                    to={`/associations/${a.association_id}`}
                                    data-testid={`association-card-${a.association_id}`}
                                    className="group block glass-card rounded-3xl overflow-hidden border border-white/10 hover:border-shariti-orange/40 transition-all"
                                >
                                    {/* Header — logo + name */}
                                    <div className="relative h-32 bg-gradient-to-br from-shariti-orange/20 via-shariti-deep-black to-shariti-black flex items-center justify-center overflow-hidden">
                                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(238,114,46,0.12),transparent)]" />
                                        {a.logo_url ? (
                                            <img
                                                src={a.logo_url}
                                                alt={a.name}
                                                className="relative max-h-20 max-w-[70%] object-contain"
                                            />
                                        ) : (
                                            <div className="relative w-16 h-16 rounded-2xl bg-shariti-orange/30 backdrop-blur flex items-center justify-center font-extrabold text-2xl text-white shadow-lg">
                                                {(a.name || "?")[0]?.toUpperCase()}
                                            </div>
                                        )}
                                        {a.video_viewed && (
                                            <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] font-extrabold tracking-[0.2em] uppercase text-shariti-orange-light bg-shariti-deep-black/80 backdrop-blur rounded-full px-2 py-0.5">
                                                <Eye className="w-3 h-3" />
                                                Vu
                                            </span>
                                        )}
                                    </div>
                                    {/* Body */}
                                    <div className="p-4 sm:p-5">
                                        <h3 className="text-base font-extrabold text-white tracking-tight mb-1 group-hover:text-shariti-orange-light transition-colors">
                                            {a.name}
                                        </h3>
                                        {a.short_description && (
                                            <p className="text-xs text-white/60 font-light leading-relaxed mb-3 line-clamp-2">
                                                {a.short_description}
                                            </p>
                                        )}
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-shariti-orange rounded-full transition-all"
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>
                                            <span className="text-[10px] font-bold text-white/55 shrink-0 tabular-nums">
                                                {progress}%
                                            </span>
                                            <ChevronRight className="w-4 h-4 text-shariti-orange/70 group-hover:translate-x-0.5 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            )}
            <p className="mt-6 text-xs text-white/40 text-center flex items-center justify-center gap-1.5">
                <Heart className="w-3 h-3 text-shariti-orange" />
                Plus tu connais ton association, plus tu inspires confiance sur le terrain.
            </p>
        </Layout>
    );
}
